import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ChannelBinding } from "@/types";

function useBindingsClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

// #182: Bindings-Listen sind als sub-resource pro User indiziert.
// Cross-Resource: betrifft auch users (User.defaultNotificationBindingId).
function useInvalidateBindings(userId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    if (userId) {
      qc.invalidateQueries({ queryKey: [...queryKeys.detail("users", userId), "bindings"] });
      qc.invalidateQueries({ queryKey: queryKeys.detail("users", userId) });
    }
  };
}

export interface CreateBindingInput {
  userId: string;
  channelId: string;
  externalId: string;
  label?: string;
  agentId?: string | null;
}

export function useCreateBinding(
  userId: string | undefined,
): UseMutationResult<ChannelBinding, Error, CreateBindingInput> {
  const getClient = useBindingsClient();
  const invalidate = useInvalidateBindings(userId);
  return useMutation({
    mutationFn: (input) => getClient().post<ChannelBinding>("/channel-bindings", input),
    onSuccess: invalidate,
  });
}

// #182 Phase 1: Admin-Verify — setzt verifiedAt.
export function useVerifyBinding(
  userId: string | undefined,
): UseMutationResult<ChannelBinding, Error, string> {
  const getClient = useBindingsClient();
  const invalidate = useInvalidateBindings(userId);
  return useMutation({
    mutationFn: (id) => getClient().post<ChannelBinding>(`/channel-bindings/${id}/verify`, {}),
    onSuccess: invalidate,
  });
}

// #182: Binding auf einen anderen Agent umhaengen (oder zurueck auf null/Fallback).
export function useSetBindingAgent(
  userId: string | undefined,
): UseMutationResult<ChannelBinding, Error, { id: string; agentId: string | null }> {
  const getClient = useBindingsClient();
  const invalidate = useInvalidateBindings(userId);
  return useMutation({
    mutationFn: ({ id, agentId }) =>
      getClient().patch<ChannelBinding>(`/channel-bindings/${id}/agent`, { agentId }),
    onSuccess: invalidate,
  });
}

export function useDeleteBinding(
  userId: string | undefined,
): UseMutationResult<{ ok: boolean }, Error, string> {
  const getClient = useBindingsClient();
  const invalidate = useInvalidateBindings(userId);
  return useMutation({
    mutationFn: (id) => getClient().delete<{ ok: boolean }>(`/channel-bindings/${id}`),
    onSuccess: invalidate,
  });
}
